import { useMemo, useState } from "react";
import { Link, useNavigate, useSearch } from "@tanstack/react-router";
import { format, parseISO } from "date-fns";
import { MoreHorizontal, Plus, RefreshCw, Search, Trash2 } from "lucide-react";
import { ConfirmDialog } from "@/components/shared/confirm-dialog";
import { EmptyState } from "@/components/shared/empty-state";
import { ErrorBoundary } from "@/components/shared/error-boundary";
import { PageHeader } from "@/components/shared/page-header";
import { StatusDot } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Input, NativeSelect } from "@/components/ui/input";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { formatMoney } from "@/features/clients/constants";
import { useClients } from "@/features/clients/api";
import { useCreditNotes, useDeleteCreditNote } from "@/features/credit-notes/api";
import { CN_STATUSES, labelFor, statusTone } from "@/features/credit-notes/constants";
import type { CreditNoteListRow, CreditNoteStatus } from "@/features/credit-notes/types";
import { isSupabaseConfigured } from "@/lib/supabase";

export function CreditNotesPage({
  clientId: clientIdProp,
  embedded,
}: { clientId?: string; embedded?: boolean } = {}) {
  return (
    <ErrorBoundary title="Credit notes crashed">
      <CreditNotesInner clientIdProp={clientIdProp} embedded={embedded} />
    </ErrorBoundary>
  );
}

function CreditNotesInner({
  clientIdProp,
  embedded,
}: {
  clientIdProp?: string;
  embedded?: boolean;
}) {
  const navigate = useNavigate();
  const search = useSearch({ strict: false }) as { client?: string };
  const clientFilter = clientIdProp ?? search.client ?? "all";

  const { data = [], isLoading, error, refetch, isFetching } = useCreditNotes(
    clientFilter !== "all" ? { clientId: clientFilter } : undefined,
  );
  const { data: clients = [] } = useClients();
  const remove = useDeleteCreditNote();

  const [statusFilter, setStatusFilter] = useState<"all" | CreditNoteStatus>("all");
  const [q, setQ] = useState("");
  const [deleting, setDeleting] = useState<CreditNoteListRow | null>(null);

  const filtered = useMemo(() => {
    const query = q.trim().toLowerCase();
    return data.filter((row) => {
      if (statusFilter !== "all" && row.status !== statusFilter) return false;
      if (!query) return true;
      return [row.cn_number, row.client_name, row.client_code, row.invoice_number, row.reason]
        .filter(Boolean)
        .join(" ")
        .toLowerCase()
        .includes(query);
    });
  }, [data, statusFilter, q]);

  const issuedValue = useMemo(
    () => data.filter((r) => r.status === "issued").reduce((s, r) => s + r.grand_total, 0),
    [data],
  );

  if (!isSupabaseConfigured) {
    return (
      <EmptyState
        title="Supabase not configured"
        description="Set VITE_SUPABASE_URL and VITE_SUPABASE_ANON_KEY."
      />
    );
  }

  return (
    <div className="space-y-3">
      {!embedded ? (
        <PageHeader
          title="Credit notes"
          description="GST adjustments · reduce AR on issue"
          actions={
            <>
              <Button type="button" variant="outline" size="sm" disabled={isFetching} onClick={() => refetch()}>
                <RefreshCw className={isFetching ? "animate-spin" : ""} />
                Refresh
              </Button>
              <Button type="button" size="sm" onClick={() => navigate({ to: "/credit-notes/new" })}>
                <Plus />
                New credit note
              </Button>
            </>
          }
        />
      ) : null}

      {!embedded ? (
        <div className="rounded border bg-card px-3 py-2 text-xs w-fit">
          Issued credits <strong className="tabular-nums">{formatMoney(issuedValue)}</strong>
        </div>
      ) : null}

      <div className="flex flex-wrap items-center gap-2">
        <div className="relative min-w-[12rem] flex-1">
          <Search className="absolute left-2 top-1/2 size-3.5 -translate-y-1/2 text-muted-foreground" />
          <Input
            className="h-8 pl-7 text-xs"
            placeholder="Search CN, client, invoice…"
            value={q}
            onChange={(e) => setQ(e.target.value)}
          />
        </div>
        {embedded || clientIdProp ? null : (
          <NativeSelect
            className="h-8 w-auto text-xs"
            value={clientFilter}
            onChange={(e) =>
              navigate({
                to: "/credit-notes",
                search: e.target.value === "all" ? {} : { client: e.target.value },
              })
            }
          >
            <option value="all">All clients</option>
            {clients.map((c) => (
              <option key={c.id} value={c.id}>
                {c.code} · {c.name}
              </option>
            ))}
          </NativeSelect>
        )}
        <NativeSelect
          className="h-8 w-auto text-xs"
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value as typeof statusFilter)}
        >
          <option value="all">All statuses</option>
          {CN_STATUSES.map((s) => (
            <option key={s.value} value={s.value}>
              {s.label}
            </option>
          ))}
        </NativeSelect>
      </div>

      {isLoading ? (
        <p className="py-12 text-center text-sm text-muted-foreground">Loading…</p>
      ) : error ? (
        <EmptyState title="Failed to load" description={(error as Error).message} />
      ) : filtered.length === 0 ? (
        <EmptyState
          title="No credit notes"
          description="Issue a credit note to adjust client receivables."
          action={
            <Button size="sm" onClick={() => navigate({ to: "/credit-notes/new" })}>
              <Plus />
              New credit note
            </Button>
          }
        />
      ) : (
        <div className="rounded-md border">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>CN #</TableHead>
                <TableHead>Client</TableHead>
                <TableHead>Issue</TableHead>
                <TableHead>Invoice</TableHead>
                <TableHead>Status</TableHead>
                <TableHead className="text-right">Amount</TableHead>
                <TableHead className="w-10" />
              </TableRow>
            </TableHeader>
            <TableBody>
              {filtered.map((row) => (
                <TableRow key={row.id} className="text-xs">
                  <TableCell>
                    <Link
                      to="/credit-notes/$cnId"
                      params={{ cnId: row.id }}
                      className="font-mono font-medium hover:underline"
                    >
                      {row.cn_number}
                    </Link>
                  </TableCell>
                  <TableCell>
                    <span className="font-mono text-muted-foreground">{row.client_code}</span>{" "}
                    {row.client_name}
                  </TableCell>
                  <TableCell>{format(parseISO(row.issue_date), "dd MMM yyyy")}</TableCell>
                  <TableCell className="font-mono text-muted-foreground">
                    {row.invoice_number ?? "—"}
                  </TableCell>
                  <TableCell>
                    <StatusDot tone={statusTone(row.status)}>
                      {labelFor(CN_STATUSES, row.status)}
                    </StatusDot>
                  </TableCell>
                  <TableCell className="text-right tabular-nums font-medium">
                    {formatMoney(row.grand_total)}
                  </TableCell>
                  <TableCell>
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="ghost" size="icon-sm">
                          <MoreHorizontal />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuItem asChild>
                          <Link to="/credit-notes/$cnId" params={{ cnId: row.id }}>
                            Open
                          </Link>
                        </DropdownMenuItem>
                        {row.status === "draft" ? (
                          <>
                            <DropdownMenuItem asChild>
                              <Link to="/credit-notes/$cnId/edit" params={{ cnId: row.id }}>
                                Edit
                              </Link>
                            </DropdownMenuItem>
                            <DropdownMenuSeparator />
                            <DropdownMenuItem className="text-destructive" onClick={() => setDeleting(row)}>
                              <Trash2 />
                              Delete draft
                            </DropdownMenuItem>
                          </>
                        ) : null}
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}

      <ConfirmDialog
        open={Boolean(deleting)}
        onOpenChange={(open) => !open && setDeleting(null)}
        title="Delete draft credit note?"
        description={`${deleting?.cn_number} will be permanently removed.`}
        confirmLabel="Delete"
        destructive
        loading={remove.isPending}
        onConfirm={async () => {
          if (!deleting) return;
          await remove.mutateAsync(deleting.id);
          setDeleting(null);
        }}
      />
    </div>
  );
}
